import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { AccountDeletionEligibility } from "../types";

const ELIGIBILITY_FIELDS = `
  eligible
  blockers {
    organizationId
    organizationName
    otherMembers {
      id
      name
      email
    }
  }
  soloOrganizations {
    organizationId
    organizationName
  }
`;

export const accountDeletionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccountDeletionEligibility: builder.query<AccountDeletionEligibility, void>({
      query: () => ({
        body: {
          query: `
            query AccountDeletionEligibility {
              accountDeletionEligibility {
                ${ELIGIBILITY_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) =>
        pick<AccountDeletionEligibility>(res, "accountDeletionEligibility"),
    }),

    requestAccountDeletion: builder.mutation<boolean, void>({
      query: () => ({
        body: {
          query: `
            mutation RequestAccountDeletion {
              requestAccountDeletion
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "requestAccountDeletion"),

      invalidatesTags: ["User"],
    }),

    cancelAccountDeletion: builder.mutation<boolean, void>({
      query: () => ({
        body: {
          query: `
            mutation CancelAccountDeletion {
              cancelAccountDeletion
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "cancelAccountDeletion"),

      invalidatesTags: ["User"],
    }),

    transferOrganizationOwnership: builder.mutation<
      boolean,
      { organizationId: string; newOwnerUserId: string }
    >({
      query: ({ organizationId, newOwnerUserId }) => ({
        body: {
          query: `
            mutation TransferOrganizationOwnership($organizationId: String!, $newOwnerUserId: String!) {
              transferOrganizationOwnership(organizationId: $organizationId, newOwnerUserId: $newOwnerUserId)
            }
          `,
          variables: { organizationId, newOwnerUserId },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "transferOrganizationOwnership"),

      // Roles changed for the new owner — refetch anything user-scoped.
      invalidatesTags: ["User"],
    }),
  }),
});

export const {
  useGetAccountDeletionEligibilityQuery,
  useLazyGetAccountDeletionEligibilityQuery,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
  useTransferOrganizationOwnershipMutation,
} = accountDeletionApi;
